import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { View } from '@zinc/ui';
import useUpdateEffect from 'bz-utilities/hooks/update-effect';
import React, { useCallback, useMemo, useState } from 'react';
import type { CustomListItem } from 'tm3-core/api.dtos';
import CustomListItemComp from './controls/custom-list-item';
import ListAdd from './controls/list-add';

export interface IProps {
  canOrder?: boolean;
  canRemove?: boolean;
  canAdd?: boolean;
  selectValues?: boolean;
  valuesToAdd?: string[];
  items: ListEditorItem[];
  maxLength?: number;
  onRemove?: (item: ListEditorItem) => void;
  onReorder?: (items: ListEditorItem[]) => void;
  onAdd?: (value: string) => void;
  category: string;
}

export type ListEditorItem =
  | CustomListItem
  | { id: string | number; text: string; order?: number; inUse?: boolean };

const ListEditor: React.FC<IProps> = React.memo(
  ({
    canOrder,
    canRemove,
    canAdd,
    selectValues,
    valuesToAdd,
    items = [],
    maxLength,
    onRemove,
    onReorder,
    onAdd = () => {},
    category
  }) => {
    const [list, setList] = useState(items);

    useUpdateEffect(() => {
      setList(items);
    }, [items]);

    const content = useMemo(() => {
      if (items.length === 0) {
        return <div className="py-2 text-gray-500">There are no items to display.</div>;
      }
      return list
        .sort((item: ListEditorItem) => item.order ?? 0)
        .map((item: ListEditorItem, i: number) => {
          return (
            <CustomListItemComp
              key={item.id ?? i}
              canOrder={canOrder}
              canRemove={canRemove}
              onRemove={onRemove}
              item={item}
              category={category}
            />
          );
        });
    }, [list, items, canOrder, canRemove, onRemove]);

    const addControl = useMemo(() => {
      if (!canAdd || (selectValues && valuesToAdd!.length === 0)) return null;

      return (
        <ListAdd
          onAdd={onAdd}
          maxLength={maxLength}
          valuesToAdd={valuesToAdd}
          selectValues={selectValues}
        />
      );
    }, [canAdd, selectValues, valuesToAdd]);

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
      })
    );

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active && active.id !== over.id) {
          const ids = list.map((l) => l.id);
          const oldIndex = ids.indexOf(active.id);
          const newIndex = ids.indexOf(over.id);

          const orderedList = arrayMove(list, oldIndex, newIndex).map((l, i) => {
            return { id: l.id, text: l.text, inUse: l.inUse, order: i };
          });

          setList(orderedList);
          onReorder?.(orderedList);
        }
      },
      [onReorder, list]
    );

    const sortedItems = useMemo(() => {
      return list.sort((i) => i.order ?? 0).map((i) => i.id);
    }, [list]);

    return (
      <View className="grid px-2">
        {addControl}
        <View className="pt-3">
          <DndContext
            modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext items={sortedItems} strategy={verticalListSortingStrategy}>
              <View
                gap="4"
                padding="2"
                className="max-h-[400px] overflow-auto"
                borderColor="neutral-faded"
                borderRadius="small"
              >
                {content}
              </View>
            </SortableContext>
          </DndContext>
        </View>
      </View>
    );
  }
);

ListEditor.displayName = 'ListEditor';

export default ListEditor;
